import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';

// Angular Material
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

// Angular Web Component
import { MatSelectFilterModule } from 'mat-select-filter';

// other imports
import { TranslateModule } from '@ngx-translate/core';
import { Ng2TelInputModule } from 'ng2-tel-input';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

// Pipes
import { DatePipe } from '@wb/core/pipes/date.pipe';
import { NumberPipe } from '@wb/core/pipes/number.pipe';
import { PercentagePipe } from '@wb/core/pipes/percentage.pipe';
import { PhonePipe } from '@wb/core/pipes/phone.pipe';
import { PricePipe } from '@wb/core/pipes/price.pipe';
import { RoundPipe } from '@wb/core/pipes/round.pipe';
import { UcFirstPipe } from '@wb/core/pipes/ucfirst.pipe';
import { SafePipe } from '../../core/pipes/safe.pipe';

// Directives
import { AutoFocusDirective } from '../../core/directives/autoFocus.directive';
import { CurrencyDirective } from '../../core/directives/currency.directive';
import { DateDirective } from '../../core/directives/date.directive';
import { EventTrackDirective } from '../../core/directives/event-track.directive';
import { MediaQueryDirective } from '../../core/directives/media-query.directive';
import { NumberDirective } from '../../core/directives/number.directive';
import { PasswordStrengthDirective } from '../../core/directives/password-strength.directive';
import { PhoneDirective } from '../../core/directives/phone.directive';
import { VinNumberDirective } from '../../core/directives/vin-number.directive';
import { ZipCodeDirective } from '../../core/directives/zipcode.directive';

// Shared Components
import { ReactiveContentDirective } from '@wb/core/directives/reactive-content.directive';
import { WebsiteDirective } from '@wb/core/directives/website.directive';
import { ClientReportDialogComponent } from '@wb/shared/components/client-report-dialog/client-report-dialog.component';
import { QuillModule } from 'ngx-quill';
import { ActivityFeedComponent } from '../components/activity-feed/activity-feed.component';
import { AssignLeadSalespersonDialogComponent } from '../components/assign-lead-salesperson-dialog/assign-lead-salesperson-dialog.component';
import { AssignSalespersonDialogComponent } from '../components/assign-salesperson-dialog/assign-salesperson.component';
import { BuildDealDialogComponent } from '../components/build-deal-dialog/build-deal-dialog.component';
import { CriteriaDialogComponent } from '../components/criteria-dialog/criteria-dialog.component';
import { CriteriaTableComponent } from '../components/criteria-table/criteria-table.component';
import { DelistDialogComponent } from '../components/delist-dialog/delist-dialog.component';
import { DialogComponent } from '../components/dialog/dialog.component';
import { FileUploadComponent } from '../components/file-upload-dialog/file-upload-dialog.component';
import { GraphEmbedFilterComponent } from '../components/graph-embed-filter/graph-embed-filter.component';
import { GraphEmbedComponent } from '../components/graph-embed/graph-embed.component';
import { GraphHeaderComponent } from '../components/graph-header/graph-header.component';
import { HeaderComponent } from '../components/header/header.component';
import { ImportDialogComponent } from '../components/import-dialog/import-dialog.component';
import { TermsComponent } from '../components/legal/terms/terms.component';
import { NoAccessComponent } from '../components/no-access/no-access.component';
import { PhoneNumberComponent } from '../components/phone-number/phone-number.component';
import { PinDialogComponent } from '../components/pin-dialog/pin-dialog.component';
import { PriceResetDialogComponent } from '../components/price-reset-dialog/price-reset-dialog.component';
import { QuillWrapperComponent } from '../components/quill-wrapper/quill-wrapper.component';
import { SearchFilterComponent } from '../components/search-filter/search-filter.component';
import { SecretDialogComponent } from '../components/secret-dialog/secret-dialog.component';
import { SelectRoleDialogComponent } from '../components/select-role-dialog/select-role-dialog.component';
import { SelectRoleTableComponent } from '../components/select-role-table/select-role-table.component';
import { StatusIconsComponent } from '../components/status-icons/status-icons.component';
import { TrashIconComponent } from '../components/trash-icon/trash-icon.component';
import { VehicleIdDialogComponent } from '../components/vehicle-id-dialog/vehicle-id-dialog.component';
import { Conversions } from '../utils/conversions';

@NgModule({
  declarations: [
    HeaderComponent,
    AssignSalespersonDialogComponent,
    AssignLeadSalespersonDialogComponent,
    ActivityFeedComponent,
    CriteriaDialogComponent,
    CriteriaTableComponent,
    DialogComponent,
    SecretDialogComponent,
    PinDialogComponent,
    ImportDialogComponent,
    DelistDialogComponent,
    PriceResetDialogComponent,
    VehicleIdDialogComponent,
    ClientReportDialogComponent,
    SelectRoleDialogComponent,
    SelectRoleTableComponent,
    FileUploadComponent,
    SearchFilterComponent,
    StatusIconsComponent,
    DatePipe,
    NumberPipe,
    PercentagePipe,
    PhonePipe,
    PricePipe,
    RoundPipe,
    SafePipe,
    UcFirstPipe,
    MediaQueryDirective,
    TermsComponent,
    PhoneDirective,
    VinNumberDirective,
    ZipCodeDirective,
    EventTrackDirective,
    NumberDirective,
    DateDirective,
    AutoFocusDirective,
    CurrencyDirective,
    WebsiteDirective,
    PasswordStrengthDirective,
    ReactiveContentDirective,
    TrashIconComponent,
    BuildDealDialogComponent,
    NoAccessComponent,
    GraphEmbedComponent,
    GraphEmbedFilterComponent,
    GraphHeaderComponent,
    PhoneNumberComponent,
    QuillWrapperComponent,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSortModule,
    MatPaginatorModule,
    MatCardModule,
    MatMenuModule,
    MatDividerModule,
    MatIconModule,
    MatBadgeModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    TranslateModule,
    MatTabsModule,
    MatListModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatDialogModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    NgxSkeletonLoaderModule,
    MatAutocompleteModule,
    Ng2TelInputModule,
    MatSelectFilterModule,
    QuillModule,
  ],
  exports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSortModule,
    MatPaginatorModule,
    MatCardModule,
    MatMenuModule,
    MatDividerModule,
    MatIconModule,
    MatBadgeModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    TranslateModule,
    MatTabsModule,
    MatListModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatDialogModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    DatePipe,
    NumberPipe,
    PercentagePipe,
    PhonePipe,
    PricePipe,
    RoundPipe,
    SafePipe,
    UcFirstPipe,
    MediaQueryDirective,
    TermsComponent,
    PhoneDirective,
    VinNumberDirective,
    ZipCodeDirective,
    EventTrackDirective,
    NumberDirective,
    DateDirective,
    CurrencyDirective,
    WebsiteDirective,
    PasswordStrengthDirective,
    ReactiveContentDirective,
    HeaderComponent,
    SelectRoleTableComponent,
    ActivityFeedComponent,
    AssignSalespersonDialogComponent,
    AssignLeadSalespersonDialogComponent,
    DialogComponent,
    SecretDialogComponent,
    PinDialogComponent,
    DelistDialogComponent,
    PriceResetDialogComponent,
    VehicleIdDialogComponent,
    ClientReportDialogComponent,
    FileUploadComponent,
    SearchFilterComponent,
    StatusIconsComponent,
    NgxSkeletonLoaderModule,
    TrashIconComponent,
    BuildDealDialogComponent,
    NoAccessComponent,
    GraphEmbedComponent,
    GraphEmbedFilterComponent,
    GraphHeaderComponent,
    CriteriaTableComponent,
    Ng2TelInputModule,
    PhoneNumberComponent,
    MatSelectFilterModule,
    QuillWrapperComponent,
  ],
  providers: [
    DatePipe,
    NumberPipe,
    PercentagePipe,
    PhonePipe,
    PricePipe,
    RoundPipe,
    SafePipe,
    UcFirstPipe,
    Conversions,
    { provide: MAT_DIALOG_DATA, useValue: {} },
  ],
})
export class SharedModule {}
